<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.order.actions.requestPayment.title')"
    :info="$t('components.order.actions.requestPayment.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.requestPayment.title'), type: 'confirm', variant: 'success', disabled: isDisabled, callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <o-form>
      <div class="-mx-4">
        <input v-model="amount" type="text" class="bg-gray-100 text-gray-700 text-xl tracking-tight text-center w-full p-4 mt-4 mb-6 dark:bg-gray-900 dark:text-gray-200">
      </div>

      <o-field-group
        v-slot="{ status }"
        :label="$t('general.details')"
        :name="$t('components.order.actions.requestPayment.detailsInfo')"
        class="mb-4 block"
      >
        <text-field v-model="details" :status="status" />
      </o-field-group>

      <div v-if="presets.length" class="mb-2 -mx-4 text-left">
        <p class="px-4 mb-1 text-sm text-gray-600">
          {{ $t('components.order.actions.requestPayment.presets') }}
        </p>
        <o-scroll :x-axis="true" :y-axis="false" allow-drag class="px-4 pb-2">
          <div class="flex">
            <template v-for="preset in presets" :key="preset.id">
              <button
                v-if="Number(preset.amount) <= Number(remainder)"

                class="w-18 h-18 rounded bg-blue-200 text-blue-600 flex-shrink-0 mr-2 text-sm p-2 transform hover:bg-blue-600 hover:text-white hover:scale-102 transition-all leading-tight dark:bg-blue-800/30 dark:hover:bg-blue-900/50 dark:text-blue-500 dark:hover:text-white"
                @click="handlePresetClick(preset)"
              >
                <p class="font-medium mb-1">
                  {{ preset.title }}
                </p>
                <p>{{ $currency.format(preset.amount) }}</p>
              </button>
            </template>
          </div>
        </o-scroll>
      </div>
    </o-form>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  TextField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:order': [value: any]
}>()

const { $api, $notification, $bus, $currency, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const amount = ref(0.00)
const remainder = ref(0.00)
const details = ref('')

const presets = computed(() => {
  const total = props.order.data.total
  const values = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50]

  const percentages = values
    .map((value) => {
      return {
        id: `percentage-${value}`,
        title: $i18n.t('components.order.actions.requestPayment.deposit', { value }),
        amount: (total * (value / 100)).toFixed(2),
      }
    })


  return [
    {
      id: 'remainder',
      title: 'Remainder',
      amount: props.order.data.totals.remainder.toFixed(2),
    },
    ...percentages,
  ]
})

const amountLabel = computed(() => {
  return $currency.format(amount.value)
})

const isDisabled = computed(() => {
  return amount.value <= 0 || !details.value.length
})

function open() {
  amount.value = props.order.data.totals.remainder.toFixed(2)
  remainder.value = props.order.data.totals.remainder.toFixed(2)

  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  try {
    const amt = amount.value
    const det = details.value
    await $api.order.requestPayment(String(props.order.idOrder), { amount: amt, details: det })

    $notification.toast({
      title: $i18n.t('components.order.actions.requestPayment.success'),
      text: $i18n.t('components.order.actions.requestPayment.successInfo'),
      variant: 'success',
    })

    try {
      const order = (await $api.order.get(String(props.order.idOrder))).data

      emit('update:order', order)
      $bus.emit.updated('Order', order)
    } catch (e: any) {
      $notification.error({ errors: e?.errors })
    }

    return true
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  } finally {
    emit('update:loading', false)
  }
}

function handlePresetClick(preset: any) {
  amount.value = preset.amount
}

defineExpose({ open })
</script>
